import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { motion } from "framer-motion";
import { 
  User, 
  Save, 
  Loader2, 
  CheckCircle2, 
  RefreshCw,
  AlertCircle,
  Shield,
  Globe,
  MapPin,
  Info,
} from "lucide-react";
import { toast } from "sonner";

import { Header } from "@/components/layout/Header";
import { formatDate } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { updateMe } from "@/lib/api/users";
import { syncFromDistant, SyncFromDistantResult, checkSyncAvailability } from "@/lib/api/admin";
import { useAuthStore } from "@/lib/auth/store";
import { getErrorMessage } from "@/lib/api/client";

const profileSchema = z.object({
  full_name: z.string().optional(),
});

type ProfileForm = z.infer<typeof profileSchema>;

export const Route = createFileRoute('/_dashboard/settings')({
  component: SettingsPage,
})

function SettingsPage() {
  const { user, setUser } = useAuthStore();

  const profileForm = useForm<ProfileForm>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      full_name: user?.full_name || "",
    },
  });

  const profileMutation = useMutation({
    mutationFn: updateMe,
    onSuccess: (data) => {
      setUser(data);
      toast.success("Profil mis à jour");
    },
    onError: (error) => toast.error("Erreur", { description: getErrorMessage(error) }),
  });

  // Check if sync is available (only in local environment)
  const { data: syncCheck } = useQuery({
    queryKey: ["sync-availability"],
    queryFn: checkSyncAvailability,
    enabled: user?.email === "jordan.guillonneau@live.fr",
  });

  const isSyncAvailable = syncCheck?.available ?? false;

  const getInitials = () => {
    if (user?.full_name) {
      return user.full_name
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase()
        .slice(0, 2);
    }
    return user?.email?.slice(0, 2).toUpperCase() || "??";
  };

  const roleDescriptions: Record<string, { label: string; color: string; description: string }> = {
    admin: {
      label: "Administrateur",
      color: "bg-error/20 text-error",
      description: "Vous avez un accès complet à la plateforme : gestion des utilisateurs, configuration et publication des simulateurs, cotations et propositions commerciales.",
    },
    manager: {
      label: "Responsable",
      color: "bg-laposte-navy/20 text-laposte-navy",
      description: "Vous pouvez gérer les utilisateurs, les simulateurs et les cotations dans votre périmètre géographique.",
    },
    user: {
      label: "Utilisateur",
      color: "bg-info/20 text-info",
      description: "Vous pouvez créer et gérer des cotations, générer des propositions commerciales et utiliser les simulateurs publiés.",
    },
    viewer: {
      label: "Consultant",
      color: "bg-muted text-muted-foreground",
      description: "Vous pouvez consulter les simulateurs et cotations existantes en lecture seule.",
    },
  };

  const roleInfo = roleDescriptions[user?.role || "user"];
  const groups = user?.groups ?? [];

  const profileContent = (
    <div className="space-y-6">
      {/* Profile card */}
      <Card>
        <CardHeader>
          <CardTitle>Informations du profil</CardTitle>
          <CardDescription>Gérez vos informations personnelles</CardDescription>
        </CardHeader>
        <CardContent>
          <form
            onSubmit={profileForm.handleSubmit((data) =>
              profileMutation.mutate(data)
            )}
            className="space-y-6"
          >
            <div className="flex items-center gap-6">
              <Avatar className="h-20 w-20">
                <AvatarImage src={user?.profile_picture || undefined} />
                <AvatarFallback className="text-xl">
                  {getInitials()}
                </AvatarFallback>
              </Avatar>
              <div>
                <p className="font-medium text-lg">{user?.full_name || "Utilisateur"}</p>
                <p className="text-sm text-muted-foreground">{user?.email}</p>
              </div>
            </div>

            <Separator />

            <div className="space-y-2">
              <Label htmlFor="full_name">Nom complet</Label>
              <Input id="full_name" {...profileForm.register("full_name")} />
            </div>

            <Button type="submit" disabled={profileMutation.isPending}>
              {profileMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Enregistrement...
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  Enregistrer
                </>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Role & permissions card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Rôle et périmètre
          </CardTitle>
          <CardDescription>Vos droits d'accès sur la plateforme</CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="flex items-start gap-4">
            <div className="space-y-2 flex-1">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-muted-foreground">Rôle :</span>
                <Badge className={roleInfo.color}>{roleInfo.label}</Badge>
              </div>
              <div className="flex items-start gap-2 p-3 rounded-lg bg-muted/50">
                <Info className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                <p className="text-sm text-muted-foreground">{roleInfo.description}</p>
              </div>
            </div>
          </div>

          <Separator />

          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-muted-foreground">Périmètre :</span>
              <div className="flex items-center gap-1.5">
                {user?.scope === "national" ? (
                  <Globe className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <MapPin className="h-4 w-4 text-muted-foreground" />
                )}
                <span className="text-sm font-medium">
                  {user?.scope === "national" ? "National" : "Régional"}
                </span>
              </div>
            </div>

            {user?.scope === "national" ? (
              <p className="text-sm text-muted-foreground">
                Vous avez accès à l'ensemble des régions et groupes.
              </p>
            ) : groups.length > 0 ? (
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">
                  Vous avez accès aux régions suivantes :
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {groups.map((g) => (
                    <Badge key={g.id} variant="outline">
                      {g.name}
                    </Badge>
                  ))}
                </div>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                Aucune région attribuée. Contactez un administrateur pour accéder aux données.
              </p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );

  return (
    <div className="flex flex-col h-full">
      <Header title="Paramètres" />

      <div className="flex-1 p-6 overflow-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-2xl mx-auto"
        >
          {isSyncAvailable ? (
            <Tabs defaultValue="profile">
              <TabsList className="mb-6">
                <TabsTrigger value="profile" className="gap-2">
                  <User className="h-4 w-4" />
                  Profil
                </TabsTrigger>
                <TabsTrigger value="sync" className="gap-2">
                  <RefreshCw className="h-4 w-4" />
                  Sync
                </TabsTrigger>
              </TabsList>

              <TabsContent value="profile">
                {profileContent}
              </TabsContent>

              <TabsContent value="sync" className="space-y-6">
                <SyncFromDistantTabContent />
              </TabsContent>
            </Tabs>
          ) : (
            profileContent
          )}
        </motion.div>
      </div>
    </div>
  );
}

// ============== Sync From Distant Database Tab ==============

function SyncFromDistantTabContent() {
  const [syncResult, setSyncResult] = useState<SyncFromDistantResult | null>(null);
  const queryClient = useQueryClient();

  const syncMutation = useMutation({
    mutationFn: syncFromDistant,
    onSuccess: (data) => {
      setSyncResult(data);
      toast.success(data.message);
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: ["references"] });
      queryClient.invalidateQueries({ queryKey: ["simulators"] });
    },
    onError: (error) => {
      toast.error("Erreur de synchronisation", { description: getErrorMessage(error) });
    },
  });

  return (
    <Card className="border-warning/30">
      <CardHeader>
        <div className="flex items-center gap-2">
          <RefreshCw className="h-5 w-5 text-warning" />
          <CardTitle>Synchronisation depuis la base distante</CardTitle>
        </div>
        <CardDescription>
          Copie toutes les données depuis la base de production vers la base locale.
          Cette opération supprime d'abord toutes les données locales (sauf les utilisateurs).
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Warning */}
        <div className="p-4 rounded-lg bg-warning/10 border border-warning/20">
          <div className="flex items-start gap-2">
            <AlertCircle className="h-5 w-5 text-warning mt-0.5" />
            <div>
              <p className="font-medium text-warning-dark dark:text-warning">Attention : Opération destructive</p>
              <p className="text-sm text-muted-foreground mt-1">
                Cette action va supprimer toutes vos données locales (simulateurs, tables de référence, cotations, etc.)
                et les remplacer par les données de la base distante. Les utilisateurs ne sont pas affectés.
              </p>
            </div>
          </div>
        </div>

        {/* Info about what will be synced */}
        <div className="p-4 rounded-lg bg-muted/50">
          <p className="text-sm font-medium mb-2">Données synchronisées :</p>
          <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
            <li>Tables de référence</li>
            <li>Simulateurs et leurs versions</li>
            <li>Groupes et champs de formulaire</li>
            <li>Groupes et champs de sortie (formules)</li>
            <li>Variables</li>
            <li>Scénarios de test</li>
            <li>Cotations</li>
          </ul>
        </div>

        {/* Sync Result */}
        {syncResult && (
          <div className="p-4 rounded-lg bg-success/10 border border-success/20">
            <div className="flex items-center gap-2 mb-3">
              <CheckCircle2 className="h-4 w-4 text-success" />
              <p className="font-medium text-sm text-success-dark dark:text-success-light">
                Synchronisation terminée
              </p>
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Tables de référence:</span>
                <span className="font-medium">{syncResult.stats.reference_tables_synced}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Simulateurs:</span>
                <span className="font-medium">{syncResult.stats.simulators_synced}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Versions:</span>
                <span className="font-medium">{syncResult.stats.versions_synced}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Groupes de variables:</span>
                <span className="font-medium">{syncResult.stats.variable_groups_synced}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Variables:</span>
                <span className="font-medium">{syncResult.stats.variables_synced}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Groupes de formulaire:</span>
                <span className="font-medium">{syncResult.stats.form_field_groups_synced}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Champs de formulaire:</span>
                <span className="font-medium">{syncResult.stats.form_fields_synced}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Groupes de sortie:</span>
                <span className="font-medium">{syncResult.stats.output_field_groups_synced}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Champs de sortie:</span>
                <span className="font-medium">{syncResult.stats.output_fields_synced}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Scénarios de test:</span>
                <span className="font-medium">{syncResult.stats.test_scenarios_synced}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Modèles de propositions:</span>
                <span className="font-medium">{syncResult.stats.proposal_templates_synced}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Cotations:</span>
                <span className="font-medium">{syncResult.stats.simulations_synced}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Propositions commerciales:</span>
                <span className="font-medium">{syncResult.stats.proposals_synced}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Versions des propositions:</span>
                <span className="font-medium">{syncResult.stats.proposal_versions_synced}</span>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-3">
              Synchronisé le {formatDate(syncResult.synced_at)}
            </p>
          </div>
        )}

        {/* Action Button */}
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button 
              variant="destructive" 
              className="w-full"
              disabled={syncMutation.isPending}
            >
              {syncMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Synchronisation en cours...
                </>
              ) : (
                <>
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Synchroniser depuis la base distante
                </>
              )}
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Confirmer la synchronisation</AlertDialogTitle>
              <AlertDialogDescription>
                Cette action va <strong>supprimer toutes les données locales</strong> (sauf les utilisateurs) 
                et les remplacer par les données de la base de production.
                <br /><br />
                Cette opération est irréversible. Êtes-vous sûr de vouloir continuer ?
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Annuler</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => syncMutation.mutate()}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                Oui, synchroniser
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </CardContent>
    </Card>
  );
}
